/**
 * NoShowDialog — Ted #32c / epic E3.2 (#1158): the dog was on today's Expected
 * list and never came. "Edison was first listed today but didn't come. How to
 * cancel his daycare reservation?"
 *
 * A no-show is a closed decision of its own — not a cancellation the client
 * made, not a visit the desk completed — so it asks for a reason (required: the
 * next person reading the day wants to know why this row is closed) and says
 * what it does NOT do: nobody is charged.
 *
 * The reason starts PREFILLED ("Did not arrive") so the common case is one tap
 * after the dialog opens, and editable for the others ("Owner called out
 * sick"). Focus lands on "Keep reservation" — a stray Enter keeps the booking.
 *
 * Label guard (#1103): the input has `id` and `name`.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { DEFAULT_NO_SHOW_REASON } from './no-show.js';

export interface NoShowDialogProps {
  /** What is being closed — dogs and owner, never an id. */
  readonly label: string;
  readonly busy?: boolean;
  /** The server's own refusal sentence, when the last attempt was refused. */
  readonly error?: string | null;
  readonly onKeep: () => void;
  readonly onConfirm: (reason: string) => void;
}

export function NoShowDialog({
  label,
  busy = false,
  error = null,
  onKeep,
  onConfirm,
}: NoShowDialogProps): JSX.Element {
  const inputId = useId();
  const keepRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState(DEFAULT_NO_SHOW_REASON);
  useEffect(() => {
    keepRef.current?.focus();
  }, []);
  const trimmed = reason.trim();

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label="Mark as a no-show"
      data-testid="no-show-dialog"
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onKeep();
      }}
    >
      <div className="ferro-blocking-dialog__panel">
        <h2>Mark as a no-show?</h2>
        <p>
          {label} didn’t arrive. The reservation closes as a no-show and comes off the
          Expected list. <strong>Nobody is charged.</strong>
        </p>
        <label className="ferro-field" htmlFor={inputId}>
          <span className="ferro-field__label">Why? (required)</span>
          <input
            id={inputId}
            name="no-show-reason"
            type="text"
            className="ferro-field__input"
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
            data-testid="no-show-reason"
          />
        </label>
        {error ? (
          <p className="ferro-form__error" role="alert" data-testid="no-show-error">
            {error}
          </p>
        ) : null}
        <div className="ferro-blocking-dialog__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            ref={keepRef}
            disabled={busy}
            onClick={onKeep}
            data-testid="no-show-dismiss"
          >
            Keep reservation
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            disabled={busy || trimmed.length < 2}
            onClick={() => onConfirm(trimmed)}
            data-testid="no-show-accept"
          >
            {busy ? 'Marking…' : 'Mark no-show'}
          </button>
        </div>
      </div>
    </div>
  );
}
